import React, { useState, useRef, useEffect } from 'react';
import Icon from '../AppIcon';
import useTranslation from '../../hooks/useTranslation';

/**
 * SearchableSelect — generic searchable combobox
 *
 * Props:
 *   options      – array of { value: string, label: string, searchText?: string }
 *   value        – currently selected value (string | null | '')
 *   onChange     – (value: string | null) => void
 *   placeholder  – string (default : t('common.searchPlaceholder'))
 *   allowNone    – boolean, prepend a "None" option (default false)
 *   className    – extra class names for the wrapper
 *   size         – 'sm' | 'md' (default 'md')
 *   disabled     – boolean
 */
const SearchableSelect = ({
  options = [],
  value,
  onChange,
  placeholder = null,
  allowNone = false,
  className = '',
  size = 'md',
  disabled = false
}) => {
  const { t } = useTranslation();
  // Le defaut ne peut pas vivre dans la signature : t() vient du hook.
  const resolvedPlaceholder = placeholder ?? t('common.searchPlaceholder');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const inputRef = useRef(null);
  const listRef = useRef(null);
  const containerRef = useRef(null);

  // Build full option list
  const allOptions = [
    ...(allowNone ? [{ value: '', label: t('common.none'), searchText: 'none' }] : []),
    ...options?.map(opt => ({
      value: opt?.value,
      label: opt?.label,
      code: opt?.code,
      searchText: (opt?.searchText || opt?.label || '')?.toLowerCase()
    }))
  ];

  // Filter by query
  const filteredOptions = query?.trim()
    ? allOptions?.filter(opt =>
        opt?.value === '' || opt?.searchText?.includes(query?.toLowerCase()?.trim())
      )
    : allOptions;

  // Resolve display value
  const selectedOption = allOptions?.find(o => String(o?.value) === String(value ?? '')) || null;
  const displayValue = open ? query : (selectedOption?.label || '');

  // Close on outside click
  useEffect(() => {
    const handleOutside = (e) => {
      if (containerRef?.current && !containerRef?.current?.contains(e?.target)) {
        setOpen(false);
        setQuery('');
        setActiveIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  // Scroll active item into view
  useEffect(() => {
    if (activeIndex >= 0 && listRef?.current) {
      const items = listRef?.current?.querySelectorAll('[data-option]');
      items?.[activeIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  const handleInputChange = (e) => {
    setQuery(e?.target?.value);
    setOpen(true);
    setActiveIndex(-1);
  };

  const handleInputFocus = () => {
    if (!disabled) {
      setOpen(true);
      setQuery('');
    }
  };

  const handleSelect = (opt) => {
    onChange(opt?.value !== '' ? opt?.value : null);
    setOpen(false);
    setQuery('');
    setActiveIndex(-1);
    inputRef?.current?.blur();
  };

  const handleKeyDown = (e) => {
    if (disabled) return;
    if (!open && (e?.key === 'ArrowDown' || e?.key === 'Enter')) {
      setOpen(true);
      return;
    }
    switch (e?.key) {
      case 'ArrowDown':
        e?.preventDefault();
        setActiveIndex(prev => Math.min(prev + 1, filteredOptions?.length - 1));
        break;
      case 'ArrowUp':
        e?.preventDefault();
        setActiveIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e?.preventDefault();
        if (activeIndex >= 0 && filteredOptions?.[activeIndex]) {
          handleSelect(filteredOptions?.[activeIndex]);
        }
        break;
      case 'Escape':
        // Liste ouverte : Echap la ferme, et la modale autour reste ouverte.
        if (open) e?.preventDefault();
        setOpen(false);
        setQuery('');
        setActiveIndex(-1);
        inputRef?.current?.blur();
        break;
      default:
        break;
    }
  };

  const handleClear = (e) => {
    e?.stopPropagation();
    onChange(null);
    setQuery('');
    inputRef?.current?.focus();
  };

  const isSm = size === 'sm';
  const inputCls = isSm
    ? 'w-full pl-2 pr-7 py-1.5 text-xs border border-gray-200 rounded-md outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white truncate disabled:opacity-50 disabled:cursor-not-allowed'
    : 'w-full pl-3 pr-8 py-2 text-xs border border-gray-200 rounded-lg outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white truncate disabled:opacity-50 disabled:cursor-not-allowed';

  const hasValue = value !== null && value !== undefined && value !== '';

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Input */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={resolvedPlaceholder}
          autoComplete="off"
          disabled={disabled}
          className={inputCls}
        />
        {/* Chevron / Clear */}
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
          {hasValue ? (
            <button
              type="button"
              onMouseDown={handleClear}
              className="p-0.5 rounded text-gray-300 hover:text-gray-500 transition-colors"
              tabIndex={-1}
            >
              <Icon name="X" size={isSm ? 10 : 12} />
            </button>
          ) : (
            <Icon name={open ? 'ChevronUp' : 'ChevronDown'} size={isSm ? 10 : 12} className="text-gray-400 pointer-events-none" />
          )}
        </div>
      </div>
      {/* Dropdown */}
      {open && !disabled && (
        <div
          ref={listRef}
          className="absolute z-[9999] mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
          style={{ minWidth: '160px', maxHeight: '220px', overflowY: 'auto' }}
        >
          {filteredOptions?.length === 0 ? (
            <div className="px-3 py-2.5 text-xs text-gray-400 italic">{t('common.noResults')}</div>
          ) : (
            filteredOptions?.map((opt, idx) => {
              const isSelected = String(value ?? '') === String(opt?.value);
              const isActive = idx === activeIndex;
              return (
                <button
                  key={opt?.value ?? 'none'}
                  data-option
                  type="button"
                  onMouseDown={() => handleSelect(opt)}
                  onMouseEnter={() => setActiveIndex(idx)}
                  className={`w-full text-left px-3 py-2 text-xs transition-colors flex items-center justify-between gap-2 ${
                    isActive ? 'bg-primary/10 text-primary' :
                    isSelected ? 'bg-orange-50 text-primary' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span className={`truncate ${opt?.value === '' ? 'text-gray-400 italic' : ''}`}>
                    {opt?.label}
                  </span>
                  {opt?.code && (
                    <span className="ml-3 px-2 py-0.5 text-xs rounded-full bg-orange-100 text-orange-600 whitespace-nowrap shrink-0">
                      {opt?.code}
                    </span>
                  )}
                  {isSelected && (
                    <Icon name="Check" size={10} className="shrink-0 text-primary" />
                  )}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
